"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { ExternalLink, MessageCircle, QrCode, X } from "lucide-react";
import { AppToastViewport, useAppToast } from "@/components/ui/app-toast";
import { apiClient } from "@/lib/api-client";
import { env } from "@/lib/env";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth-store";
import { ChatWindow } from "./chat-window";
import { ConversationList } from "./conversation-list";
import type {
  WhatsappConversation,
  WhatsappConversationsResponse,
  WhatsappMessage,
  WhatsappMessagesResponse,
} from "../types";

type EvolutionQrData = {
  qrImageDataUrl: string;
  pairingCode: string;
  status: string;
};

type EnviarMensagemResponse = {
  envio: unknown;
  conversa: WhatsappConversation;
  mensagem?: WhatsappMessage | null;
};

const PANEL_CONVERSAS_QUERY_KEY = ["whatsapp-floating-conversas"] as const;
const BADGE_CONVERSAS_QUERY_KEY = [
  "whatsapp-floating-conversas-badge",
] as const;
const FLOATING_BUTTON_POSITION_STORAGE_KEY =
  "masterapp-whatsapp-floating-button-position";
const FLOATING_BUTTON_MARGIN = 16;
const FLOATING_BUTTON_DRAG_THRESHOLD = 6;

type FloatingButtonPosition = {
  x: number;
  y: number;
};

type FloatingButtonDragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  moved: boolean;
};

export function WhatsappFloatingInbox() {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { toasts, showToast, removeToast } = useAppToast();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [floatingButtonPosition, setFloatingButtonPosition] =
    useState<FloatingButtonPosition | null>(null);
  const [isDraggingFloatingButton, setIsDraggingFloatingButton] =
    useState(false);
  const [showQrPanel, setShowQrPanel] = useState(false);
  const [qrData, setQrData] = useState<EvolutionQrData | null>(null);

  const isOpenRef = useRef(isOpen);
  const selectedConversationIdRef = useRef<string | null>(
    selectedConversationId,
  );
  const notifiedInboundMessagesRef = useRef<Set<string>>(new Set());
  const floatingButtonRef = useRef<HTMLButtonElement | null>(null);
  const floatingButtonDragRef = useRef<FloatingButtonDragState | null>(null);
  const ignoreFloatingButtonClickRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchDebounced(search.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const ocultarNoModuloWhatsapp = pathname === "/whatsapp";
  const habilitado = Boolean(user) && !ocultarNoModuloWhatsapp;
  const temRodapeDeAcao = Boolean(pathname?.startsWith("/entrada-veiculo"));

  useEffect(() => {
    if (!habilitado || typeof window === "undefined") {
      return;
    }

    const storedPosition = readFloatingButtonPosition();
    const initialPosition =
      storedPosition ??
      getDefaultFloatingButtonPosition(
        floatingButtonRef.current,
        temRodapeDeAcao,
      );

    setFloatingButtonPosition(
      clampFloatingButtonPosition(initialPosition, floatingButtonRef.current),
    );
  }, [habilitado, temRodapeDeAcao]);

  useEffect(() => {
    if (!habilitado || typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      setFloatingButtonPosition((current) =>
        current
          ? clampFloatingButtonPosition(current, floatingButtonRef.current)
          : current,
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [habilitado]);

  const whatsappConfigQuery = useQuery({
    queryKey: ["whatsapp-floating-config"],
    enabled: habilitado && isOpen,
    staleTime: 60_000,
    queryFn: async () => {
      const response = await apiClient.get<
        Array<{ key: string; value: unknown }>
      >("/admin/configuracoes");
      return response.data;
    },
  });

  const evolutionConfigurado = useMemo(() => {
    const setting = whatsappConfigQuery.data?.find(
      (s) => s.key === "whatsapp_config",
    );
    if (!setting?.value || typeof setting.value !== "object") return false;
    const c = setting.value as Record<string, unknown>;
    return (
      String(c.provider ?? "") === "evolution" &&
      Boolean(c.evolutionBaseUrl) &&
      Boolean(c.evolutionApiKeyConfigured || c.evolutionApiKey) &&
      Boolean(c.evolutionInstance)
    );
  }, [whatsappConfigQuery.data]);

  const obterQrMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get<Record<string, unknown>>(
        "/admin/whatsapp/evolution/connect",
      );
      return response.data;
    },
    onSuccess: (data) => {
      setQrData(extrairQrEvolution(data));
    },
  });

  const handleConectar = () => {
    setQrData(null);
    setShowQrPanel(true);
    obterQrMutation.mutate();
  };

  const badgeConversationsQuery = useQuery({
    queryKey: BADGE_CONVERSAS_QUERY_KEY,
    enabled: habilitado,
    queryFn: async () => {
      const response = await apiClient.get<WhatsappConversationsResponse>(
        "/admin/whatsapp/conversas",
        {
          params: {
            page: 1,
            limit: 100,
          },
        },
      );
      return response.data;
    },
    refetchInterval: 30_000,
  });

  const conversationsQuery = useQuery({
    queryKey: [...PANEL_CONVERSAS_QUERY_KEY, searchDebounced],
    enabled: habilitado && isOpen,
    queryFn: async () => {
      const response = await apiClient.get<WhatsappConversationsResponse>(
        "/admin/whatsapp/conversas",
        {
          params: {
            search: searchDebounced || undefined,
            page: 1,
            limit: 100,
          },
        },
      );
      return response.data;
    },
    refetchInterval: 30_000,
  });

  const conversations = useMemo(
    () => conversationsQuery.data?.items ?? [],
    [conversationsQuery.data],
  );

  const unreadCount = useMemo(
    () =>
      (badgeConversationsQuery.data?.items ?? []).reduce(
        (total, conversation) => total + Number(conversation.unreadCount ?? 0),
        0,
      ),
    [badgeConversationsQuery.data],
  );

  const effectiveSelectedConversationId = useMemo(() => {
    if (!conversations.length) {
      return null;
    }

    const stillExists = conversations.some(
      (conversation) => conversation.id === selectedConversationId,
    );
    if (selectedConversationId && stillExists) {
      return selectedConversationId;
    }

    return conversations[0].id;
  }, [conversations, selectedConversationId]);

  const selectedConversationFromList = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation.id === effectiveSelectedConversationId,
      ) ?? null,
    [conversations, effectiveSelectedConversationId],
  );

  const conversationQuery = useQuery({
    queryKey: ["whatsapp-floating-conversa", effectiveSelectedConversationId],
    enabled: Boolean(effectiveSelectedConversationId) && isOpen,
    queryFn: async () => {
      const response = await apiClient.get<WhatsappConversation>(
        `/admin/whatsapp/conversas/${effectiveSelectedConversationId}`,
      );
      return response.data;
    },
  });

  const activeConversation =
    conversationQuery.data ?? selectedConversationFromList;
  const isChatView = showMobileChat && Boolean(activeConversation);

  const messagesQuery = useQuery({
    queryKey: ["whatsapp-floating-mensagens", effectiveSelectedConversationId],
    enabled: Boolean(effectiveSelectedConversationId) && isOpen,
    queryFn: async () => {
      const response = await apiClient.get<WhatsappMessagesResponse>(
        `/admin/whatsapp/conversas/${effectiveSelectedConversationId}/mensagens`,
        {
          params: {
            page: 1,
            limit: 200,
          },
        },
      );
      return response.data;
    },
  });

  const updateConversationCaches = (
    conversation: WhatsappConversation,
    moveToTop = true,
  ) => {
    queryClient.setQueryData(
      ["whatsapp-floating-conversa", conversation.id],
      conversation,
    );

    queryClient.setQueryData<WhatsappConversationsResponse>(
      BADGE_CONVERSAS_QUERY_KEY,
      (current) => {
        if (!current) {
          return current;
        }
        const without = current.items.filter(
          (item) => item.id !== conversation.id,
        );
        return {
          ...current,
          items: moveToTop
            ? [conversation, ...without]
            : [...without, conversation],
        };
      },
    );

    queryClient.setQueriesData<WhatsappConversationsResponse>(
      { queryKey: PANEL_CONVERSAS_QUERY_KEY },
      (current) => {
        if (!current) {
          return current;
        }
        const without = current.items.filter(
          (item) => item.id !== conversation.id,
        );
        return {
          ...current,
          items: moveToTop
            ? [conversation, ...without]
            : [...without, conversation],
        };
      },
    );
  };

  const markAsReadMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await apiClient.post<WhatsappConversation>(
        `/admin/whatsapp/conversas/${conversationId}/marcar-lida`,
      );
      return response.data;
    },
    onSuccess: (conversation) => {
      updateConversationCaches(conversation, false);
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (payload: {
      conversationId: string;
      message: string;
    }) => {
      const response = await apiClient.post<EnviarMensagemResponse>(
        `/admin/whatsapp/conversas/${payload.conversationId}/mensagens`,
        {
          mensagem: payload.message,
        },
      );
      return response.data;
    },
    onMutate: async (variables) => {
      setSendError(null);
      const queryKey = [
        "whatsapp-floating-mensagens",
        variables.conversationId,
      ];
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<WhatsappMessagesResponse>(queryKey);

      const optimisticMessage: WhatsappMessage = {
        id: `temp-${Date.now()}`,
        conversationId: variables.conversationId,
        direction: "outbound",
        messageType: "text",
        status: "sent",
        textBody: variables.message,
        payload: {},
        metaMessageId: null,
        webhookEventId: null,
        fromPhone: null,
        toPhone: null,
        sentAt: new Date().toISOString(),
        receivedAt: null,
        readAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueryData<WhatsappMessagesResponse>(
        queryKey,
        (current) => {
          if (!current) {
            return {
              items: [optimisticMessage],
              total: 1,
              page: 1,
              limit: 200,
            };
          }
          return {
            ...current,
            items: [...current.items, optimisticMessage],
            total: current.total + 1,
          };
        },
      );

      return {
        previous,
        optimisticMessageId: optimisticMessage.id,
      };
    },
    onError: (error, variables, context) => {
      setSendError(
        resolveApiError(error, "Não foi possível enviar a mensagem."),
      );
      if (context?.previous) {
        queryClient.setQueryData(
          ["whatsapp-floating-mensagens", variables.conversationId],
          context.previous,
        );
      }
    },
    onSuccess: (response, variables, context) => {
      const queryKey = [
        "whatsapp-floating-mensagens",
        variables.conversationId,
      ];
      const serverMessage = response.mensagem ?? null;

      if (serverMessage) {
        queryClient.setQueryData<WhatsappMessagesResponse>(
          queryKey,
          (current) => {
            if (!current) {
              return {
                items: [serverMessage],
                total: 1,
                page: 1,
                limit: 200,
              };
            }
            const withoutOptimistic = current.items.filter(
              (item) => item.id !== context?.optimisticMessageId,
            );
            const alreadyExists = withoutOptimistic.some(
              (item) => item.id === serverMessage.id,
            );
            return {
              ...current,
              items: alreadyExists
                ? withoutOptimistic.map((item) =>
                    item.id === serverMessage.id ? serverMessage : item,
                  )
                : [...withoutOptimistic, serverMessage],
            };
          },
        );
      } else {
        queryClient.invalidateQueries({ queryKey });
      }

      if (response.conversa) {
        updateConversationCaches(response.conversa);
      }

      queryClient.invalidateQueries({ queryKey: PANEL_CONVERSAS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BADGE_CONVERSAS_QUERY_KEY });
    },
  });

  useEffect(() => {
    if (!habilitado || !user) {
      return;
    }

    const socket = io(`${env.apiUrl}/whatsapp`, {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socket.on(
      "whatsapp:conversation.updated",
      (conversation: WhatsappConversation) => {
        const currentBadge =
          queryClient.getQueryData<WhatsappConversationsResponse>(
            BADGE_CONVERSAS_QUERY_KEY,
          );
        const before = currentBadge?.items.find(
          (item) => item.id === conversation.id,
        );
        const increasedUnread =
          Number(conversation.unreadCount ?? 0) >
          Number(before?.unreadCount ?? 0);

        updateConversationCaches(conversation);

        if (increasedUnread && !isOpenRef.current) {
          const label =
            conversation.contactName?.trim() ||
            formatPhone(conversation.contactPhone);
          showToast({
            title: `Nova mensagem de ${label}`,
            message:
              conversation.lastMessagePreview?.trim() ||
              "Você recebeu uma nova mensagem no WhatsApp.",
            variant: "info",
            durationMs: 5000,
          });
        }
      },
    );

    socket.on("whatsapp:message.created", (message: WhatsappMessage) => {
      const currentSelectedConversationId = selectedConversationIdRef.current;
      if (message.conversationId === currentSelectedConversationId) {
        queryClient.setQueryData<WhatsappMessagesResponse>(
          ["whatsapp-floating-mensagens", currentSelectedConversationId],
          (current) => {
            if (!current) {
              return {
                items: [message],
                total: 1,
                page: 1,
                limit: 200,
              };
            }
            if (current.items.some((item) => item.id === message.id)) {
              return current;
            }
            return {
              ...current,
              items: [...current.items, message],
              total: current.total + 1,
            };
          },
        );
      }

      if (
        message.direction === "inbound" &&
        !notifiedInboundMessagesRef.current.has(message.id) &&
        (!isOpenRef.current ||
          message.conversationId !== currentSelectedConversationId)
      ) {
        notifiedInboundMessagesRef.current.add(message.id);
        if (notifiedInboundMessagesRef.current.size > 250) {
          notifiedInboundMessagesRef.current.clear();
        }

        const badgeData =
          queryClient.getQueryData<WhatsappConversationsResponse>(
            BADGE_CONVERSAS_QUERY_KEY,
          );
        const conversation = badgeData?.items.find(
          (item) => item.id === message.conversationId,
        );
        const label = conversation
          ? conversation.contactName?.trim() ||
            formatPhone(conversation.contactPhone)
          : "cliente";
        const body = message.textBody?.trim();
        if (!body) {
          return;
        }

        showToast({
          title: `WhatsApp: ${label}`,
          message: body,
          variant: "info",
          durationMs: 5000,
        });
      }

      queryClient.invalidateQueries({ queryKey: BADGE_CONVERSAS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PANEL_CONVERSAS_QUERY_KEY });
    });

    socket.on("whatsapp:message.status.updated", (message: WhatsappMessage) => {
      const currentSelectedConversationId = selectedConversationIdRef.current;
      if (message.conversationId !== currentSelectedConversationId) {
        return;
      }
      queryClient.setQueryData<WhatsappMessagesResponse>(
        ["whatsapp-floating-mensagens", currentSelectedConversationId],
        (current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            items: current.items.map((item) =>
              item.id === message.id ? { ...item, ...message } : item,
            ),
          };
        },
      );
    });

    return () => {
      socket.disconnect();
    };
  }, [habilitado, queryClient, showToast, user]);

  const handleToggle = () => {
    setIsOpen((current) => !current);
    if (!isOpen) {
      setSendError(null);
      setShowMobileChat(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowMobileChat(false);
    setSendError(null);
  };

  const handleSelectConversation = (conversation: WhatsappConversation) => {
    setSelectedConversationId(conversation.id);
    setShowMobileChat(true);
    setSendError(null);
  };

  const handleSendMessage = async (message: string) => {
    if (!effectiveSelectedConversationId) {
      return;
    }
    await sendMessageMutation.mutateAsync({
      conversationId: effectiveSelectedConversationId,
      message,
    });
  };

  const handleMarkAsRead = async () => {
    if (!activeConversation || activeConversation.unreadCount <= 0) {
      return;
    }
    await markAsReadMutation.mutateAsync(activeConversation.id);
  };

  const handleFloatingButtonPointerDown = (
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const button = floatingButtonRef.current;
    const position =
      floatingButtonPosition ??
      getDefaultFloatingButtonPosition(button, temRodapeDeAcao);
    const clampedPosition = clampFloatingButtonPosition(position, button);

    floatingButtonDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: clampedPosition.x,
      originY: clampedPosition.y,
      moved: false,
    };

    setFloatingButtonPosition(clampedPosition);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleFloatingButtonPointerMove = (
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    const dragState = floatingButtonDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    const moved =
      Math.abs(deltaX) > FLOATING_BUTTON_DRAG_THRESHOLD ||
      Math.abs(deltaY) > FLOATING_BUTTON_DRAG_THRESHOLD;

    if (!dragState.moved && moved) {
      dragState.moved = true;
      ignoreFloatingButtonClickRef.current = true;
      setIsDraggingFloatingButton(true);
    }

    if (!dragState.moved) {
      return;
    }

    event.preventDefault();
    setFloatingButtonPosition(
      clampFloatingButtonPosition(
        {
          x: dragState.originX + deltaX,
          y: dragState.originY + deltaY,
        },
        floatingButtonRef.current,
      ),
    );
  };

  const handleFloatingButtonPointerEnd = (
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    const dragState = floatingButtonDragRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    floatingButtonDragRef.current = null;
    setIsDraggingFloatingButton(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (dragState.moved) {
      setFloatingButtonPosition((current) => {
        const clamped = clampFloatingButtonPosition(
          current ?? {
            x: dragState.originX,
            y: dragState.originY,
          },
          floatingButtonRef.current,
        );
        persistFloatingButtonPosition(clamped);
        return clamped;
      });

      window.setTimeout(() => {
        ignoreFloatingButtonClickRef.current = false;
      }, 0);
    }
  };

  const handleFloatingButtonClick = (
    event: ReactMouseEvent<HTMLButtonElement>,
  ) => {
    if (ignoreFloatingButtonClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    handleToggle();
  };

  const badgeStatus = resolveApiStatus(badgeConversationsQuery.error);
  if (!habilitado || badgeStatus === 401 || badgeStatus === 403) {
    return null;
  }

  return (
    <>
      <AppToastViewport
        toasts={toasts}
        onClose={removeToast}
        className="top-20 z-[90]"
      />

      {isOpen ? (
        <button
          type="button"
          aria-label="Fechar inbox do WhatsApp"
          className="fixed inset-0 z-[78] bg-slate-900/40 backdrop-blur-[1px] lg:hidden"
          onClick={handleClose}
        />
      ) : null}

      {isOpen ? (
        <div className="fixed inset-x-3 bottom-24 top-20 z-[80] overflow-hidden rounded-[10px] border border-[#e5e7eb] bg-white shadow-2xl shadow-slate-900/25 lg:inset-auto lg:bottom-24 lg:right-6 lg:top-auto lg:h-[72vh] lg:w-[min(980px,calc(100vw-2rem))]">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                Inbox WhatsApp
              </p>
              <p className="truncate text-xs text-slate-500">
                {unreadCount > 0
                  ? `${unreadCount} mensagem(ns) não lida(s)`
                  : "Sem mensagens não lidas"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {evolutionConfigurado ? (
                <button
                  type="button"
                  onClick={handleConectar}
                  disabled={obterQrMutation.isPending}
                  className="inline-flex h-8 items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-60"
                  title="Exibir QR Code para conectar o WhatsApp"
                >
                  <QrCode className="h-3.5 w-3.5" />
                  {obterQrMutation.isPending ? "Aguarde..." : "Conectar"}
                </button>
              ) : null}
              <Link
                href="/whatsapp"
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Tela completa
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                onClick={handleClose}
                aria-label="Fechar inbox do WhatsApp"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="relative h-[calc(100%-3.5rem)] min-h-0 overflow-hidden">
            {showQrPanel ? (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-white p-6">
                <button
                  type="button"
                  onClick={() => setShowQrPanel(false)}
                  className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100"
                  aria-label="Fechar QR Code"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-sm font-semibold text-slate-900">
                  Conectar WhatsApp
                </p>
                {obterQrMutation.isPending ? (
                  <p className="text-xs text-slate-500">Obtendo QR Code...</p>
                ) : obterQrMutation.isError ? (
                  <div className="space-y-2 text-center">
                    <p className="text-xs text-rose-600">
                      {resolveApiError(
                        obterQrMutation.error,
                        "Não foi possível obter o QR Code.",
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => obterQrMutation.mutate()}
                      className="text-xs font-semibold text-[#E8441A] hover:underline"
                    >
                      Tentar novamente
                    </button>
                  </div>
                ) : qrData?.status === "open" ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-xs text-emerald-800">
                    WhatsApp já está conectado.
                  </div>
                ) : qrData?.qrImageDataUrl ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="overflow-hidden rounded-[10px] border border-[#e5e7eb] bg-white p-2">
                      <Image
                        src={qrData.qrImageDataUrl}
                        alt="QR Code WhatsApp"
                        width={200}
                        height={200}
                        unoptimized
                        className="h-48 w-48 object-contain"
                      />
                    </div>
                    {qrData.pairingCode ? (
                      <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 font-mono text-base font-semibold tracking-widest text-slate-900">
                        {qrData.pairingCode}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => obterQrMutation.mutate()}
                      className="text-xs font-semibold text-[#E8441A] hover:underline"
                    >
                      Atualizar QR Code
                    </button>
                  </div>
                ) : qrData ? (
                  <p className="text-xs text-slate-500">
                    A Evolution não retornou QR Code. Verifique se a instância
                    foi criada.
                  </p>
                ) : null}
              </div>
            ) : null}
            <div
              className={cn(
                "absolute inset-0 p-3 transition-transform duration-300 ease-out",
                isChatView
                  ? "-translate-x-full pointer-events-none"
                  : "translate-x-0",
              )}
            >
              <ConversationList
                conversations={conversations}
                selectedConversationId={effectiveSelectedConversationId}
                search={search}
                isLoading={conversationsQuery.isLoading}
                errorMessage={
                  conversationsQuery.isError
                    ? resolveApiError(
                        conversationsQuery.error,
                        "Não foi possível carregar as conversas.",
                      )
                    : null
                }
                onSearchChange={setSearch}
                onSelectConversation={handleSelectConversation}
              />
            </div>

            <div
              className={cn(
                "absolute inset-0 p-3 transition-transform duration-300 ease-out",
                isChatView
                  ? "translate-x-0"
                  : "translate-x-full pointer-events-none",
              )}
            >
              <ChatWindow
                conversation={activeConversation}
                messages={messagesQuery.data?.items ?? []}
                isLoadingMessages={
                  messagesQuery.isLoading || conversationQuery.isLoading
                }
                isSending={sendMessageMutation.isPending}
                containerClassName="h-full min-h-0"
                errorMessage={
                  sendError ??
                  (messagesQuery.isError
                    ? resolveApiError(
                        messagesQuery.error,
                        "Não foi possível carregar as mensagens.",
                      )
                    : null)
                }
                showBackButton={isChatView}
                onBack={() => setShowMobileChat(false)}
                onSendMessage={handleSendMessage}
                onMarkAsRead={handleMarkAsRead}
              />
            </div>
          </div>
        </div>
      ) : null}

      <button
        ref={floatingButtonRef}
        type="button"
        onClick={handleFloatingButtonClick}
        onPointerDown={handleFloatingButtonPointerDown}
        onPointerMove={handleFloatingButtonPointerMove}
        onPointerUp={handleFloatingButtonPointerEnd}
        onPointerCancel={handleFloatingButtonPointerEnd}
        style={
          floatingButtonPosition
            ? {
                left: `${floatingButtonPosition.x}px`,
                top: `${floatingButtonPosition.y}px`,
              }
            : undefined
        }
        aria-label={
          isOpen ? "Fechar inbox do WhatsApp" : "Abrir inbox do WhatsApp"
        }
        title="Arraste para reposicionar"
        className={cn(
          "fixed z-[82] inline-flex h-12 touch-none select-none items-center gap-2 rounded-full border border-emerald-300 bg-emerald-500 px-4 text-sm font-semibold text-white shadow-xl shadow-emerald-900/20 transition-colors hover:bg-emerald-600",
          floatingButtonPosition ? null : "right-20 lg:right-6",
          !floatingButtonPosition &&
            (temRodapeDeAcao ? "bottom-40" : "bottom-6"),
          isDraggingFloatingButton ? "cursor-grabbing" : "cursor-grab",
        )}
      >
        <span className="relative inline-flex">
          <MessageCircle className="h-5 w-5" />
          {unreadCount > 0 ? (
            <span className="absolute -right-2.5 -top-2.5 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </span>
        <span className="hidden sm:inline">
          {isOpen ? "Fechar chat" : "WhatsApp"}
        </span>
      </button>
    </>
  );
}

function extrairQrEvolution(data: Record<string, unknown>): EvolutionQrData {
  const find = (obj: unknown, keys: string[]): string => {
    if (!obj || typeof obj !== "object") return "";
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const v = find(item, keys);
        if (v) return v;
      }
      return "";
    }
    const map = obj as Record<string, unknown>;
    for (const [k, v] of Object.entries(map)) {
      if (keys.some((key) => key.toLowerCase() === k.toLowerCase())) {
        const s = typeof v === "string" ? v.trim() : "";
        if (s) return s;
      }
    }
    for (const v of Object.values(map)) {
      const r = find(v, keys);
      if (r) return r;
    }
    return "";
  };

  const rawQr = find(data, [
    "qrImageDataUrl",
    "base64",
    "qrOrCode",
    "qrcode",
    "qrCode",
  ]);
  const qrImageDataUrl = rawQr.startsWith("data:image/")
    ? rawQr
    : rawQr.length > 100 && /^[A-Za-z0-9+/=]+$/.test(rawQr)
      ? `data:image/png;base64,${rawQr}`
      : "";

  return {
    qrImageDataUrl,
    pairingCode: find(data, ["pairingCode", "pairing_code"]),
    status: find(data, ["state", "status", "connectionState"]).toLowerCase(),
  };
}

function resolveApiError(error: unknown, fallback: string): string {
  const message =
    (error as { response?: { data?: { message?: string | string[] } } })
      ?.response?.data?.message ?? null;

  if (Array.isArray(message)) {
    return message.join(", ");
  }

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return fallback;
}

function readFloatingButtonPosition(): FloatingButtonPosition | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(
      FLOATING_BUTTON_POSITION_STORAGE_KEY,
    );
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<FloatingButtonPosition>;
    if (
      typeof parsed.x !== "number" ||
      typeof parsed.y !== "number" ||
      !Number.isFinite(parsed.x) ||
      !Number.isFinite(parsed.y)
    ) {
      return null;
    }

    return {
      x: parsed.x,
      y: parsed.y,
    };
  } catch {
    return null;
  }
}

function persistFloatingButtonPosition(position: FloatingButtonPosition) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      FLOATING_BUTTON_POSITION_STORAGE_KEY,
      JSON.stringify(position),
    );
  } catch {
    /* Ignora falhas de persistencia local do navegador */
  }
}

function getDefaultFloatingButtonPosition(
  button: HTMLElement | null,
  hasActionFooter: boolean,
): FloatingButtonPosition {
  if (typeof window === "undefined") {
    return { x: FLOATING_BUTTON_MARGIN, y: FLOATING_BUTTON_MARGIN };
  }

  const rect = button?.getBoundingClientRect();
  const buttonWidth = rect?.width ?? 150;
  const buttonHeight = rect?.height ?? 48;
  const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
  const rightOffset = isDesktop ? 24 : 80;
  const bottomOffset = hasActionFooter ? 160 : 24;

  return clampFloatingButtonPosition(
    {
      x: window.innerWidth - buttonWidth - rightOffset,
      y: window.innerHeight - buttonHeight - bottomOffset,
    },
    button,
  );
}

function clampFloatingButtonPosition(
  position: FloatingButtonPosition,
  button: HTMLElement | null,
): FloatingButtonPosition {
  if (typeof window === "undefined") {
    return position;
  }

  const rect = button?.getBoundingClientRect();
  const buttonWidth = rect?.width ?? 150;
  const buttonHeight = rect?.height ?? 48;
  const maxX = Math.max(
    FLOATING_BUTTON_MARGIN,
    window.innerWidth - buttonWidth - FLOATING_BUTTON_MARGIN,
  );
  const maxY = Math.max(
    FLOATING_BUTTON_MARGIN,
    window.innerHeight - buttonHeight - FLOATING_BUTTON_MARGIN,
  );

  return {
    x: Math.min(Math.max(position.x, FLOATING_BUTTON_MARGIN), maxX),
    y: Math.min(Math.max(position.y, FLOATING_BUTTON_MARGIN), maxY),
  };
}

function resolveApiStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return typeof status === "number" ? status : null;
}

function formatPhone(phone: string): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length < 12) {
    return phone;
  }
  const country = digits.slice(0, 2);
  const ddd = digits.slice(2, 4);
  const start = digits.slice(4, 9);
  const end = digits.slice(9);
  return `+${country} (${ddd}) ${start}-${end}`;
}
